import React from "react";
import OfficeModal from "./OfficeModal";

import * as styles from "./styles/ChooseOptionModal.css";
import * as modalStyles from "./styles/OfficeModalsGeneral.css";
import {Button, ButtonVariant} from "../../core/view/Button";

type OptionButton = {
	text: string;
	onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
	variant?: ButtonVariant;
	className?: string;
};

type ChooseOptionModalProps = {
	title: string | React.ReactNode;
	message: string | React.ReactNode;
	buttons: OptionButton[];
	onClose: () => void;
	// TODO: when OfficeModal is updated, we can use the levelType from there
	levelType?: "default" | "overBar";
	portalContainer?: string;
};

/**
 * Legacy props inherited from the old ChooseOptionModal component.  In my opinion
 * a button object for each button is a cleaner interface and I would prefer
 * we use that going forward. So.
 *
 * TODO: Remove this once all dependencies on this interface are removed.
 */
type LegacyButtonProps = {
	buttonTexts: string[];
	buttonClickHandlers: ((event: React.MouseEvent<HTMLButtonElement>) => void)[];
	redButtonIndices?: number[];
};

type LegacyChooseOptionModalProps = Omit<ChooseOptionModalProps, "buttons"> &
	LegacyButtonProps;

/**
 * Given a set of legacy button props, compose an array of OptionButton
 * objects for parsing by the component.
 * @param legacyProps - The legacy props to compose the button objects from.
 * @returns An array of button objects in the shape of OptionButton.
 */
const composeLegacyButtonProps: (
	legacyProps: LegacyButtonProps
) => OptionButton[] = (legacyProps: LegacyButtonProps) => {
	return legacyProps.buttonTexts.map((text, index) => ({
		text,
		onClick: legacyProps.buttonClickHandlers[index],
		variant: legacyProps.redButtonIndices?.includes(index)
			? "secondary"
			: "primary",
	}));
};

export const ChooseOptionModal = ({
	title,
	message,
	onClose,
	levelType = "overBar",
	portalContainer,
	...props
}: ChooseOptionModalProps | LegacyChooseOptionModalProps) => {
	const buttons: OptionButton[] =
		"buttons" in props ? props.buttons : composeLegacyButtonProps(props);

	// semantic HTML things: if the title is a string, use an h2 tag, otherwise use a div to wrap it
	// same for the message
	const TitleWrapper = typeof title === "string" ? "h2" : "div";
	const MessageWrapper = typeof message === "string" ? "p" : "div";

	return (
		<OfficeModal
			onClose={onClose}
			closeOnEsc={true}
			portalContainer={portalContainer}
			level={levelType}
			modalClass={styles.main}
			closeClass={modalStyles.closeButtonSmall}
		>
			<section className={styles.container}>
				<TitleWrapper className={styles.title}>{title}</TitleWrapper>
				<MessageWrapper className={styles.message}>{message}</MessageWrapper>
				<div className={styles.buttons}>
					{buttons.map(button => (
						<Button
							key={button.text}
							onClick={button.onClick}
							className={[styles.button, button.className]
								.filter(Boolean)
								.join(" ")}
							variant={button.variant}
						>
							{button.text}
						</Button>
					))}
				</div>
			</section>
		</OfficeModal>
	);
};
